import { createApp } from "vue";
import App from "./App.vue";
import "@/styles/main.scss";
import pusher from "pusher-js";
window.Pusher = pusher;
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import "./plugins/axios.js";
import axios from "./plugins/axios";
import echo from "./plugins/echo";
import setup from "./plugins/setup";
import debug from "./plugins/debug";
import { initTawkIfEnabled } from "./plugins/tawk.js";
import store from "./store";
import { fetchProctoredAssessmentSession } from "./utils/proctoredRouteBootstrap.js";
import { PROCTORED_OTU_SESSION_STORAGE_KEY } from "./constants/storageKeys.js";
import { proctoredTokenFromPathname } from "./utils/proctoredPath.js";

initTawkIfEnabled();

/**
 * Proctored return URL: call sanctum before vue-router loads (router import was running too early).
 * @returns {Promise<boolean>} false if we hard-redirected — do not mount the app
 */
async function bootstrapProctoredReturnUrlIfNeeded() {
    const token = proctoredTokenFromPathname();
    if (!token) {
        return true;
    }
    if (sessionStorage.getItem(PROCTORED_OTU_SESSION_STORAGE_KEY) === token) {
        return true;
    }
    store.commit("setPageLoading", true);
    try {
        await fetchProctoredAssessmentSession(token);
        sessionStorage.setItem(PROCTORED_OTU_SESSION_STORAGE_KEY, token);
        return true;
    } catch (error) {
        sessionStorage.removeItem(PROCTORED_OTU_SESSION_STORAGE_KEY);
        const status = error.response && error.response.status;
        if (status === 401) {
            try {
                store.commit("clear", null);
            } catch (e) {
                /* ignore */
            }
            window.location.replace("/login");
            return false;
        }
        window.location.replace("/");
        return false;
    } finally {
        store.commit("setPageLoading", false);
    }
}

Bugsnag.start({
    apiKey: import.meta.env.VITE_BUG_SNAG_API_KEY,
    plugins: [new BugsnagPluginVue()],
});

async function start() {
    const proceed = await bootstrapProctoredReturnUrlIfNeeded();
    if (!proceed) {
        return;
    }

    const router = (await import("./router/index.js")).default;

    const app = createApp(App)
        .use(Bugsnag.getPlugin("vue"))
        .use(setup)
        .use(store)
        .use(router)
        .use(axios)
        .use(debug)
        .use(echo);

    app.mount("#app");
}

start().catch((err) => {
    console.error(err);
    store.commit("setPageLoading", false);
});
